import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import './css/EventWorldMap.css';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in Leaflet with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const EventWorldMap = ({ events }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersLayerRef = useRef(null);

  // Use events from props or empty array - no mock data
  const eventLocations = events || [];

  // Initialize map once on mount
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    const map = L.map(mapRef.current, {
      center: [20, 0],
      zoom: 2,
      minZoom: 2,
      maxZoom: 18,
      worldCopyJump: true
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      minZoom: 2,
      maxZoom: 18
    }).addTo(map);

    mapInstanceRef.current = map;

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  // Update markers whenever events change — never destroy the map
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    console.log('[EventWorldMap] Received eventLocations:', eventLocations);

    // Remove old markers
    if (markersLayerRef.current) {
      markersLayerRef.current.forEach((m) => map.removeLayer(m));
      markersLayerRef.current = [];
    } else {
      markersLayerRef.current = [];
    }

    const coords = [];
    if (eventLocations.length > 0) {
      eventLocations.forEach((location, idx) => {
        const lat = Number(location.lat);
        const lng = Number(location.lng);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
          console.warn(`[EventWorldMap] Skipping invalid coordinates at index ${idx}`);
          return;
        }

        const icon = L.divIcon({
          className: '',
          html: `<div style="width:28px;height:28px;background:#EFB034FF;border:3px solid #fff;border-radius:50%;box-shadow:0 3px 10px rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;color:#fff;font-weight:bold;font-size:11px;font-family:sans-serif;">${idx + 1}</div>`,
          iconSize: [28, 28],
          iconAnchor: [14, 14],
          popupAnchor: [0, -14],
        });
        const marker = L.marker([lat, lng], { icon }).addTo(map);

        marker.bindPopup(`
          <div class="popup-content">
            <h4>${location.location || location.city || 'Unknown Location'}</h4>
            <p>${location.events || 0} events</p>
          </div>
        `);

        markersLayerRef.current.push(marker);
        coords.push([lat, lng]);
      });

      console.log(`[EventWorldMap] Added ${coords.length} markers`);

      if (coords.length === 1) {
        map.setView(coords[0], 10);
      } else if (coords.length > 1) {
        map.fitBounds(coords, { padding: [40, 40], maxZoom: 12, animate: false });
      }

      map.invalidateSize();

      // Auto-open first popup so user can confirm markers exist
      if (markersLayerRef.current[0]) {
        markersLayerRef.current[0].openPopup();
      }
    }
  }, [eventLocations]);

  return (
    <div className="event-world-map-container">
      <div className="world-map">
        <div 
          ref={mapRef} 
          className="leaflet-map"
          style={{ height: '400px', width: '100%' }}
        />
      </div>
    </div>
  );
};

export default EventWorldMap;
